/**
 * §6.3 engine choice — selectable cards in plain language, never
 * "cascaded vs realtime vs gemini". Includes the "no voice cloning" note on
 * both speech-to-speech engines and the §9.4 large-knowledge warning when the
 * agent's ready documents exceed the inject threshold.
 */

import type { VoiceEngine } from "../../lib/types";
import { cx } from "../../lib/format";
import { KNOWLEDGE_INJECT_TOKEN_THRESHOLD } from "./constants";

interface EngineCard {
  engine: VoiceEngine;
  title: string;
  tagline: string;
  latency: string;
  note?: string;
}

const CARDS: EngineCard[] = [
  {
    engine: "gemini",
    title: "Natural (Google)",
    tagline: "Fast and natural at a fraction of Realtime's cost.",
    latency: "~0.4s",
    note: "No voice cloning — you pick from a fixed set of voices.",
  },
  {
    engine: "cascaded",
    title: "Standard",
    tagline: "Lower cost, any voice, wide model choice.",
    latency: "~0.6s",
  },
  {
    engine: "realtime",
    title: "Realtime (OpenAI)",
    tagline: "Fastest and most natural, limited voices, highest cost.",
    latency: "~0.4s",
    note: "No voice cloning — you pick from a fixed set of voices.",
  },
];

/** Engines that look knowledge up mid-response — drives the §9.4 warning. */
const SPEECH_TO_SPEECH: VoiceEngine[] = ["realtime", "gemini"];

export interface EngineCardsProps {
  value: VoiceEngine;
  onChange: (engine: VoiceEngine) => void;
  /** SUM(token_count) of this agent's ready documents — drives the §9.4 warning. */
  readyKnowledgeTokens: number;
}

export function EngineCards({ value, onChange, readyKnowledgeTokens }: EngineCardsProps) {
  const largeKnowledge = readyKnowledgeTokens > KNOWLEDGE_INJECT_TOKEN_THRESHOLD;

  return (
    <div className="flex flex-col gap-3">
      <div role="radiogroup" aria-label="Voice engine" className="grid gap-3 sm:grid-cols-3">
        {CARDS.map((card) => {
          const selected = card.engine === value;
          return (
            <button
              key={card.engine}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(card.engine)}
              className={cx(
                "flex flex-col gap-1.5 rounded border p-4 text-left transition-colors",
                selected
                  ? "border-bright bg-surface-raised"
                  : "border-line bg-surface hover:border-muted",
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span
                  className={cx(
                    "font-display text-base tracking-display",
                    selected ? "text-bright" : "text-body",
                  )}
                >
                  {card.title}
                </span>
                <span className="font-mono text-xs text-muted">responds in {card.latency}</span>
              </span>
              <span className="text-xs text-body">{card.tagline}</span>
              {card.note && <span className="text-xs text-muted">{card.note}</span>}
            </button>
          );
        })}
      </div>

      {SPEECH_TO_SPEECH.includes(value) && largeKnowledge && (
        <p className="rounded border border-warning/60 bg-warning/10 px-3 py-2 text-xs text-warning">
          This agent has a large knowledge base (~{Math.round(readyKnowledgeTokens / 1000)}k
          tokens). On this engine, big knowledge is looked up mid-response, which can erase the
          speed advantage — Standard may actually feel faster for this agent.
        </p>
      )}
    </div>
  );
}
